/**
 * UNDO-TRASH-LIGHT (L1) — pegamento de la pila de deshacer con la UI.
 *
 * Una única pila por sesión de UI (módulo, como el store de toasts):
 *   - `registerUndo(...)` la llaman las mutaciones de dominio tras escribir:
 *     apila la inversa y muestra la pill coalescida "hecho · Deshacer".
 *   - `undoLastAction()` (botón de la pill o Ctrl/Cmd+Z) aplica la inversa por
 *     el write-path normal y confirma con el flash "Deshecho".
 *   - `resetUndoScope(userId)` vacía la pila cuando cambia la cuenta activa:
 *     las inversas capturan la BD/sesión con la que se crearon y NUNCA deben
 *     cruzar de cuenta.
 *
 * Pieza COPIABLE a otras apps: solo depende del módulo agnóstico de shared y
 * del sistema de toasts local.
 */

import { createUndoStack } from "@bso/shared";
import { dismissUndoFlash, showSavedFlash, showToast, showUndoFlash } from "@/components/ui/toast";

const undoStack = createUndoStack();

/** Textos ya traducidos que acompañan a un undo (la pila es agnóstica de i18n). */
export interface UndoMessages {
  /** Qué se acaba de hacer, p. ej. "Hábito eliminado". */
  done: string;
  /** Etiqueta del botón, p. ej. "Deshacer". */
  undoLabel: string;
  /** Confirmación tras deshacer, p. ej. "Deshecho". */
  undone: string;
  /** Error si la inversa falla, p. ej. "No se pudo deshacer". */
  failed: string;
}

/**
 * Registra la inversa de una mutación recién aplicada y muestra la pill
 * deshacible. `inverse` debe re-entrar por el write-path normal (mutaciones
 * PowerSync/Drizzle), nunca tocar estado en memoria.
 */
export function registerUndo(messages: UndoMessages, inverse: () => Promise<void>): void {
  undoStack.push({
    label: messages.done,
    undo: async () => {
      try {
        await inverse();
        showSavedFlash(messages.undone);
      } catch (err: unknown) {
        console.error("[undo]", err instanceof Error ? err.message : String(err));
        showToast(messages.failed, "error");
        throw err;
      }
    },
  });
  showUndoFlash(messages.done, {
    label: messages.undoLabel,
    onAction: () => {
      void undoLastAction();
    },
  });
}

/** Deshace la última acción (pill o atajo Ctrl/Cmd+Z). No lanza. */
export async function undoLastAction(): Promise<void> {
  dismissUndoFlash();
  // El feedback (éxito/error) ya lo emite el wrapper de cada entrada.
  await undoStack.undoLast().catch(() => undefined);
}

export function undoStackSize(): number {
  return undoStack.size();
}

let undoScopeUserId: string | undefined;

/**
 * Fija el "dueño" de la pila. Si la cuenta activa cambia (multi-cuenta,
 * login/logout, invitado→usuario), la pila se vacía y la pill desaparece.
 */
export function resetUndoScope(userId: string | undefined): void {
  if (userId === undoScopeUserId) {
    return;
  }
  undoScopeUserId = userId;
  undoStack.clear();
  dismissUndoFlash();
}
